import { useRef, useState } from 'react';

import { useSortable } from '@dnd-kit/react/sortable';

import type {
  QuizLocale,
  SetV2QuizEditorState,
  V2QuizEditorState,
} from '../state';

import BuildQuizzesQuestionCardV2 from './BuildQuizzesQuestionCardV2';

interface SortableQuestionCardV2Props {
  questionId: string;
  questionIndex: number;
  quiz: V2QuizEditorState;
  setQuiz: SetV2QuizEditorState;
  userLanguage: QuizLocale;
  isSaving: boolean;
  validationMode: boolean;
  setValidationMode: (value: boolean) => void;
  trySave: (state: V2QuizEditorState, questionIndex: number | null) => void;
  registerTranslate: (key: string, abort: AbortController) => void;
  clearTranslate: (key: string, abort: AbortController) => void;
  canReorder: boolean;
  isCollapsed: boolean;
  isAnyDragActive: boolean;
  isDropTarget: boolean;
  moveQuestionUp: (questionIndex: number) => void;
  moveQuestionDown: (questionIndex: number) => void;
  isDisplayLanguageMismatch: boolean;
  isDraft?: boolean;
  onDiscardDraft?: () => void;
}

const SortableQuestionCardV2 = ({
  questionId,
  questionIndex,
  canReorder,
  isCollapsed,
  isAnyDragActive,
  isDropTarget,
  moveQuestionUp,
  moveQuestionDown,
  isDraft = false,
  onDiscardDraft,
  ...cardProps
}: SortableQuestionCardV2Props) => {
  const [element, setElement] = useState<Element | null>(null);
  const handleRef = useRef<HTMLElement | null>(null);
  const { isDragSource } = useSortable({
    id: questionId,
    index: questionIndex,
    element,
    // Always pass the grip: without a handle, dnd-kit puts role="button" and
    // aria-disabled on the card wrapper, which disables every field inside it.
    handle: handleRef,
    disabled: !canReorder,
  });

  return (
    <div data-question-id={questionId} ref={setElement}>
      <BuildQuizzesQuestionCardV2
        {...cardProps}
        questionIndex={questionIndex}
        handleRef={handleRef}
        canReorder={canReorder}
        isDragSource={isDragSource}
        isCollapsed={isCollapsed}
        isAnyDragActive={isAnyDragActive}
        isDropTarget={isDropTarget}
        moveQuestionUp={moveQuestionUp}
        moveQuestionDown={moveQuestionDown}
        isDraft={isDraft}
        onDiscardDraft={onDiscardDraft}
      />
    </div>
  );
};

export default SortableQuestionCardV2;
